/**
 * @description Channel Partner landed on app. instead of admin. (LYN-329)
 *
 * @dependencies
 * - BrandingContext occupancy (SO first, then CP)
 * - lynxBuildHostname.buildChannelPartnerAdminHostname
 *
 * @notes
 * - Copy shows this partner's real admin hostname — never "admin.*" and
 *   never a hardcoded TowTech example.
 * - Occupancy kind must be channel_partner. SO-under-CP app.{so} is not
 *   steered to a parent CP admin host.
 * - Vanity (verified) wins: admin.{cp_domain}. Else factory
 *   admin.{slug}.staging.lynx.build / admin.{slug}.lynx.build.
 * - Do not change buildNativeOrigin / app. product origin.
 */

import { useBranding } from '../../contexts/BrandingContext';
import {
  buildChannelPartnerAdminHostname,
  isLynxBuildStagingHostname
} from '../../utils/lynxBuildHostname';

const FALLBACK_HEADING = 'Please sign in on this partner\'s admin host';
const FALLBACK_BODY = 'Channel Partner admin lives on the admin host for this partner. This host is the live application.';

/**
 * User-visible steer copy from occupancy + current-env factory.
 *
 * @param {object|null|undefined} occupancy
 * @param {{ staging?: boolean, isLoading?: boolean }} [options]
 * @returns {{ heading: string, body: string, adminHost: string|null }}
 */
export function adminHostRequiredCopy(occupancy, { staging = false, isLoading = false } = {}) {
  if (isLoading) {
    return { heading: FALLBACK_HEADING, body: FALLBACK_BODY, adminHost: null };
  }
  const adminHost = buildChannelPartnerAdminHostname(occupancy, { staging });
  if (!adminHost) {
    return { heading: FALLBACK_HEADING, body: FALLBACK_BODY, adminHost: null };
  }
  return {
    heading: `Please sign in using ${adminHost}`,
    body: `Channel Partner admin lives on ${adminHost}. This host is the live application.`,
    adminHost
  };
}

export default function ChannelPartnerAdminHostRequired() {
  const { occupancy, isLoading } = useBranding();
  const staging = typeof window !== 'undefined'
    ? isLynxBuildStagingHostname(window.location.hostname)
    : false;
  const { heading, body } = adminHostRequiredCopy(occupancy, {
    staging,
    isLoading
  });

  return (
    <div className="p-6 max-w-xl">
      <h1 className="text-2xl font-bold text-gray-900">{heading}</h1>
      <p className="text-sm text-gray-500 mt-2">{body}</p>
    </div>
  );
}
